import React, { useCallback, useContext, useMemo } from "react";
import MapboxDraw, { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { Feature, LineString, Point } from "geojson";
import rbush from "geojson-rbush";
import mapboxgl from "mapbox-gl";
import { passing_draw_polygon as passingDrawMode } from "mapbox-gl-draw-passing-mode";

import { DRAWING_CONSTANTS } from "fond/constants";
import DrawHandler from "fond/draw/DrawHandler";
import directSelect from "fond/draw/modes/design/underground/directSelect";
import drawLineString from "fond/draw/modes/design/underground/drawLineString";
import drawPoint from "fond/draw/modes/design/underground/drawPoint";
import { isValid } from "fond/draw/modes/design/underground/helper";
import simpleSelect from "fond/draw/modes/design/underground/simpleSelectUG";
import ugDrawStyles from "fond/draw/modes/design/underground/ugDrawStyles";
import { SNAP_INDICATOR_SOURCE } from "fond/draw/snapIndicator";
import { MapContext } from "fond/map/MapProvider";
import {
  DeleteButton,
  LineStringButton,
  PointButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  SnappingButton,
  ToolbarConfig,
  UndoButton,
} from "fond/map/Toolbar";
import { useSnapIndicator } from "fond/map/useSnapIndicator";
import { LayerIds, MuPowerEditableLayerIds, MuPowerInputLayerIds } from "fond/types";
import { isPoint } from "fond/types/geojson";

const toolbarConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [<SelectButton key="select" />, <PolygonSelectButton key="selectPolygon" />],
  },
  {
    id: "drawTools",
    type: "action",
    items: [<PointButton key="drawPoint" />, <LineStringButton key="drawLineString" />],
  },
  {
    id: "snappingTools",
    type: "action",
    items: [<SnappingButton key="snap" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

const modes = {
  simple_select: simpleSelect,
  direct_select: directSelect,
  draw_point: drawPoint,
  draw_line_string: drawLineString,
  passing_draw_polygon: passingDrawMode,
};

interface MuPowerUndergroundHandlerProps {
  initialFeatures: Feature<LineString | Point>[];
}

/**
 * Layer keys from the vector tile source to include as snap targets
 * when drawing/editing ug_path lines.
 */
const snapTargetLayers = [MuPowerEditableLayerIds.ugPoints, MuPowerInputLayerIds.poles, MuPowerInputLayerIds.addresses];

const MuPowerUndergroundHandler: React.FC<MuPowerUndergroundHandlerProps> = ({ initialFeatures }) => {
  const { drawControl, map } = useContext(MapContext);

  useSnapIndicator(map);

  const validatedFeatures: Feature<LineString | Point>[] = useMemo(() => {
    const tree = rbush();
    tree.load(initialFeatures);

    return tree.all().features.map((f) => {
      if (f.properties) f.properties[`${DRAWING_CONSTANTS.Valid}`] = String(isValid(f, tree));
      return f;
    });
  }, [initialFeatures]);

  const snapGetFeatures = useCallback((m: mapboxgl.Map, draw: MapboxDraw) => {
    const tileFeatures = snapTargetLayers.flatMap((layerKey) => m.querySourceFeatures("project-layers", { sourceLayer: layerKey }));
    return [...draw.getAll().features, ...tileFeatures];
  }, []);

  const options: MapboxDrawOptions = useMemo(
    () =>
      ({
        allLayerIds: [LayerIds.inStreet, LayerIds.inStreetPoints],
        editingLayerIds: [LayerIds.inStreet],
        snap: true,
        snapIndicatorSource: SNAP_INDICATOR_SOURCE,
        snapOptions: {
          snapPx: 25,
          snapToMidPoints: false,
          snapVertexPriorityDistance: 0.0025,
          snapGetFeatures,
        },
      }) as MapboxDrawOptions,
    [snapGetFeatures]
  );

  const getFeatureProperties = useCallback(
    (feature: Feature<LineString | Point>) => {
      const tree = rbush();
      tree.load(drawControl?.current.getAll() || []);

      return {
        layerId: isPoint(feature) ? LayerIds.inStreetPoints : LayerIds.inStreet,
        [DRAWING_CONSTANTS.Valid]: drawControl && isValid(feature, tree).toString(),
      };
    },
    [drawControl]
  );

  return (
    <DrawHandler
      startingMode={validatedFeatures.length > 0 ? "simple_select" : "draw_line_string"}
      initialFeatures={validatedFeatures}
      styles={ugDrawStyles}
      config={toolbarConfig}
      getFeatureProperties={getFeatureProperties}
      options={options}
      modes={modes}
    />
  );
};

export default MuPowerUndergroundHandler;
